import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ModalShell from '@shared/components/ModalShell';
import AlarmDetail from '@/components/AlarmDetail';
import { linkAccentClass } from '@/lib/uiClasses';
import { notifyAlarmChanged } from '@/lib/useMonitorAlarms';
import type { MonitorAlarmDetail } from '@/services/api';

const OPEN_ALARM = 'open-alarm-detail';

// Como en `EventDetailModal`: una petición que llega antes de que el modal
// escuche (Monitor con `?alarm=` montándose en el mismo commit que el layout)
// no se pierde.
let pendingId: number | null = null;
let pendingOnClose: (() => void) | null = null;
// La alarma que el modal enseña (o va a enseñar). Decide qué hacer con el
// `onClose` que ya había al pedir otra apertura.
let shownId: number | null = null;

/**
 * Abre el detalle de una alarma desde cualquier pantalla. `onClose` corre al
 * cerrarlo (Monitor lo usa para quitar `?alarm=` de la URL).
 *
 * Sin `onClose` no se pierde el que ya había: si es la MISMA alarma (el botón
 * «Alarma #N» de uno de sus eventos) se conserva, y el `?alarm=` se quitará al
 * cerrar; si es OTRA, se ejecuta ya, porque la URL dejó de describir lo que se
 * ve. Con `onClose` nuevo el anterior se descarta sin ejecutarlo: quien lo pasa
 * (Monitor con otro `?alarm=`) ya es dueño de la URL, y ejecutar el viejo
 * borraría el parámetro recién puesto.
 */
export function openAlarmDetail(id: number, onClose?: () => void) {
  const prev = pendingOnClose;
  if (onClose) {
    pendingOnClose = onClose;
  } else if (id !== shownId) {
    pendingOnClose = null;
    prev?.();
  }
  pendingId = id;
  shownId = id;
  window.dispatchEvent(new CustomEvent<number>(OPEN_ALARM, { detail: id }));
}

/**
 * El detalle de una alarma en un modal. Montado UNA vez en el layout y abierto
 * con `openAlarmDetail(id)` (evento de ventana `open-alarm-detail`). El
 * contenido es `AlarmDetail`, el mismo de la página `/monitor/alarmas/:id`.
 * Con el z-index por defecto de `ModalShell` (z-50) a propósito: el menú de
 * «Silenciar» (`OverflowMenu`, z-60) tiene que quedar por encima. El detalle de
 * evento (z-[110]), que se abre desde su lista, queda encima de éste.
 */
export default function AlarmDetailModal() {
  const [alarmId, setAlarmId] = useState<number | null>(null);
  const [loaded, setLoaded] = useState<MonitorAlarmDetail | null>(null);
  // Con la misma alarma `AlarmDetail` no se vuelve a montar ni a cargar, así que
  // vaciar `loaded` dejaría el título en «Alarma» para siempre.
  const idRef = useRef<number | null>(null);

  useEffect(() => {
    const handleOpen = (e: Event) => {
      const id = (e as CustomEvent<number>).detail;
      pendingId = null;
      if (idRef.current !== id) setLoaded(null);
      idRef.current = id;
      setAlarmId(id);
    };
    window.addEventListener(OPEN_ALARM, handleOpen);
    if (pendingId != null) {
      idRef.current = pendingId;
      setAlarmId(pendingId);
      pendingId = null;
    }
    return () => window.removeEventListener(OPEN_ALARM, handleOpen);
  }, []);

  const close = useCallback(() => {
    const onClose = pendingOnClose;
    pendingId = null;
    pendingOnClose = null;
    shownId = null;
    idRef.current = null;
    setAlarmId(null);
    setLoaded(null);
    onClose?.();
  }, []);

  return (
    <ModalShell
      isOpen={alarmId != null}
      onClose={close}
      title={loaded?.title || 'Alarma'}
      subtitle={alarmId != null ? `#${alarmId}` : undefined}
      maxWidth="max-w-4xl"
      headerActions={
        alarmId != null ? (
          <Link to={`/monitor/alarmas/${alarmId}`} onClick={close} className={`${linkAccentClass} text-xs whitespace-nowrap`}>
            Abrir en página
          </Link>
        ) : undefined
      }
    >
      {alarmId != null ? (
        <AlarmDetail
          key={alarmId}
          alarmId={alarmId}
          layout="modal"
          onLoaded={setLoaded}
          onNavigate={close}
          onChanged={notifyAlarmChanged}
        />
      ) : null}
    </ModalShell>
  );
}
